// API Service for making HTTP requests
import config from '../config';

// Helper function to get API base URL (accesses getter each time)
const getApiBaseURL = () => config.api.baseURL;

/**
 * Get CSRF cookie from Sanctum (required for stateful authentication)
 * @unused Reserved for future use
 */
// eslint-disable-next-line no-unused-vars
async function getCsrfCookie() {
  try {
    // Sanctum CSRF endpoint is at /sanctum/csrf-cookie (not under /api)
    // Extract base URL (without /api)
    const baseUrl = getApiBaseURL().replace('/api', '');
    const csrfUrl = `${baseUrl}/sanctum/csrf-cookie`;
    
    const response = await fetch(csrfUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    // Wait a bit to ensure cookie is set
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // CSRF cookie is set via Set-Cookie header, no need to parse response
    return response;
  } catch (error) {
    console.error('Failed to get CSRF cookie:', error);
    // Continue anyway, might work without it in some configurations
  }
}

/**
 * Get XSRF token from cookies
 * @unused Reserved for future use
 */
// eslint-disable-next-line no-unused-vars
function getXSRFToken() {
  try {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'XSRF-TOKEN' && value) {
        return decodeURIComponent(value);
      }
    }
  } catch (error) {
    console.error('Error reading XSRF token:', error);
  }
  return null;
}

/**
 * Generic API request function
 */
export async function apiRequest(endpoint, options = {}) {
  const url = `${getApiBaseURL()}${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(url, mergedOptions);
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      let errorData = null;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
        errorData = errorJson; // Store full error object for detailed handling
      } catch (e) {
        // If response is not JSON, use the text or default message
        if (errorText) {
          errorMessage = errorText;
        }
      }
      
      // Create error object with response data attached
      const apiError = new Error(errorMessage);
      apiError.response = {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
      };
      throw apiError;
    }

    return await response.json();
  } catch (error) {
    // Silently handle network errors to avoid console spam during background polling
    // Network errors are common and expected during temporary connectivity issues
    const isNetworkError = error instanceof TypeError || 
                          (error.message && (
                            error.message.includes('Failed to fetch') || 
                            error.message.includes('NetworkError') ||
                            error.message.includes('Network request failed')
                          ));
    
    // Only log non-network errors
    if (!isNetworkError) {
      if (error.message) {
        console.error('API Request failed:', error.message);
        if (error.response) {
          console.error('Error response:', error.response);
        }
      } else {
        console.error('API Request failed:', error);
      }
    }
    // Always throw the error so callers can handle it
    throw error;
  }
}

/**
 * Fetch products with pagination (public - active products only)
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Products per page (default: 12)
 */
export async function getProducts(page = 1, perPage = 12) {
  try {
    const response = await apiRequest(`/products?page=${page}&per_page=${perPage}`);
    // Handle both old format (array) and new format (paginated object)
    if (Array.isArray(response)) {
      return {
        data: response,
        current_page: 1,
        per_page: response.length,
        total: response.length,
        last_page: 1,
        has_more: false
      };
    }
    return response;
  } catch (error) {
    console.error('Failed to fetch products:', error);
    throw error;
  }
}

/**
 * Fetch all products including inactive (admin only)
 */
export async function getAdminProducts(token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const products = await apiRequest('/admin/products', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return products;
  } catch (error) {
    console.error('Failed to fetch admin products:', error);
    throw error;
  }
}

/**
 * Fetch a single product by ID
 */
export async function getProduct(id) {
  try {
    const product = await apiRequest(`/products/${id}`);
    return product;
  } catch (error) {
    console.error(`Failed to fetch product ${id}:`, error);
    throw error;
  }
}

/**
 * Fetch all categories
 */
export async function getCategories() {
  try {
    const categories = await apiRequest('/categories');
    return categories;
  } catch (error) {
    console.error('Failed to fetch categories:', error);
    // Return empty array if categories endpoint doesn't exist yet
    return [];
  }
}

/**
 * Fetch all categories (admin - includes inactive)
 */
export async function getAdminCategories(token) {
  try {
    const categories = await apiRequest('/admin/categories', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return categories;
  } catch (error) {
    console.error('Failed to fetch admin categories:', error);
    throw error;
  }
}

/**
 * Get a single category
 */
export async function getCategory(id) {
  try {
    const category = await apiRequest(`/categories/${id}`);
    return category;
  } catch (error) {
    console.error(`Failed to fetch category ${id}:`, error);
    throw error;
  }
}

/**
 * Create a category (admin only)
 */
export async function createCategory(categoryData, token) {
  try {
    // Check if there's an image file to upload
    const hasImageFile = categoryData.image && categoryData.image instanceof File;
    
    if (hasImageFile) {
      // Use FormData for file uploads
      const formData = new FormData();
      
      // Append all fields to FormData
      formData.append('name', categoryData.name || '');
      if (categoryData.description) {
        formData.append('description', categoryData.description);
      }
      formData.append('image', categoryData.image);
      formData.append('is_active', categoryData.is_active ? '1' : '0');
      if (categoryData.parent_id !== null && categoryData.parent_id !== undefined) {
        formData.append('parent_id', String(categoryData.parent_id));
      }
      
      // Use fetch directly for FormData (don't use apiRequest which expects JSON)
      const url = `${getApiBaseURL()}/categories`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
          // Include validation errors if present
          if (errorJson.errors) {
            const errorMessages = Object.values(errorJson.errors).flat();
            errorMessage = errorMessages.join(', ') || errorMessage;
          }
        } catch (e) {
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } else {
      // No file upload, use JSON - exclude image field if it's null
      const jsonData = { ...categoryData };
      if (jsonData.image === null || jsonData.image === undefined) {
        delete jsonData.image;
      }
      
      const result = await apiRequest('/categories', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });
      return result;
    }
  } catch (error) {
    console.error('Failed to create category:', error);
    throw error;
  }
}

/**
 * Update a category (admin only)
 */
export async function updateCategory(id, categoryData, token) {
  try {
    // Check if there's an image file to upload
    const hasImageFile = categoryData.image && categoryData.image instanceof File;
    
    if (hasImageFile) {
      // Use FormData for file uploads
      const formData = new FormData();
      
      // Append all fields to FormData
      formData.append('name', categoryData.name || '');
      if (categoryData.description) {
        formData.append('description', categoryData.description);
      }
      formData.append('image', categoryData.image);
      formData.append('is_active', categoryData.is_active ? '1' : '0');
      if (categoryData.parent_id !== null && categoryData.parent_id !== undefined) {
        formData.append('parent_id', String(categoryData.parent_id));
      }
      
      // Use POST with _method=PUT for FormData updates (Laravel standard way)
      formData.append('_method', 'PUT');
      
      // Use fetch directly for FormData (don't use apiRequest which expects JSON)
      const url = `${getApiBaseURL()}/categories/${id}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
          // Include validation errors if present
          if (errorJson.errors) {
            const errorMessages = Object.values(errorJson.errors).flat();
            errorMessage = errorMessages.join(', ') || errorMessage;
          }
        } catch (e) {
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } else {
      // No file upload, use JSON - exclude image field if it's null
      const jsonData = { ...categoryData };
      if (jsonData.image === null || jsonData.image === undefined) {
        delete jsonData.image;
      }
      
      const result = await apiRequest(`/categories/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });
      return result;
    }
  } catch (error) {
    console.error(`Failed to update category ${id}:`, error);
    throw error;
  }
}

/**
 * Delete a category (admin only)
 */
export async function deleteCategory(id, token) {
  try {
    const result = await apiRequest(`/categories/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error(`Failed to delete category ${id}:`, error);
    throw error;
  }
}

/**
 * Register a new user
 */
export async function register(name, email, password, password_confirmation, phone = null) {
  try {
    const payload = {
      name,
      email,
      password,
      password_confirmation,
    };
    if (phone != null && String(phone).trim() !== '') {
      payload.phone = String(phone).trim();
    }
    const response = await apiRequest('/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response;
  } catch (error) {
    console.error('Failed to register:', error);
    throw error;
  }
}

/**
 * Login user
 */
export async function login(email, password) {
  try {
    const response = await apiRequest('/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
      }),
    });
    return response;
  } catch (error) {
    console.error('Failed to login:', error);
    throw error;
  }
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email) {
  try {
    const response = await apiRequest('/password/reset-request', {
      method: 'POST',
      body: JSON.stringify({
        email,
      }),
    });
    return response;
  } catch (error) {
    console.error('Failed to request password reset:', error);
    throw error;
  }
}

/**
 * Reset password with OTP
 */
export async function resetPassword(email, otp, password, password_confirmation) {
  try {
    const response = await apiRequest('/password/reset', {
      method: 'POST',
      body: JSON.stringify({
        email,
        otp,
        password,
        password_confirmation,
      }),
    });
    return response;
  } catch (error) {
    console.error('Failed to reset password:', error);
    throw error;
  }
}

/**
 * Logout user
 */
export async function logout(token) {
  try {
    await apiRequest('/logout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error('Failed to logout:', error);
    throw error;
  }
}

/**
 * Get authenticated user
 */
export async function getMe(token) {
  try {
    const user = await apiRequest('/me', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return user;
  } catch (error) {
    console.error('Failed to get user:', error);
    throw error;
  }
}

/**
 * Update user profile
 */
export async function updateProfile(profileData, token) {
  try {
    // Check if profile_picture is a File (for file uploads)
    const hasFile = profileData.profile_picture instanceof File;
    
    if (hasFile) {
      // Use FormData for file uploads
      const formData = new FormData();
      
      console.log('📤 Creating FormData for profile picture upload', {
        file: profileData.profile_picture,
        fileName: profileData.profile_picture?.name,
        fileType: profileData.profile_picture?.type,
        fileSize: profileData.profile_picture?.size
      });
      
      // Append the file FIRST
      formData.append('profile_picture', profileData.profile_picture);
      
      // Append any other profile fields (excluding the file)
      Object.keys(profileData).forEach(key => {
        if (key !== 'profile_picture') {
          const value = profileData[key];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, String(value));
          }
        }
      });
      
      // Debug: Log FormData contents
      console.log('📤 FormData entries:', Array.from(formData.entries()).map(([key, value]) => ({
        key,
        value: value instanceof File ? `[File: ${value.name}, ${value.size} bytes]` : value
      })));
      
      // Use POST for file uploads (Laravel doesn't handle files in PUT requests properly)
      // The backend has a POST route specifically for FormData uploads
      const url = `${getApiBaseURL()}/profile`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
          
          // Handle validation errors
          if (errorJson.errors) {
            const errorMessages = Object.values(errorJson.errors).flat();
            errorMessage = errorMessages.join(', ');
          }
          
          // Handle 401 specifically
          if (response.status === 401) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            errorMessage = 'Your session has expired. Please log in again.';
          }
        } catch (e) {
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } else {
      // Use JSON for regular profile updates without files
      const result = await apiRequest('/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });
      return result;
    }
  } catch (error) {
    console.error('Failed to update profile:', error);
    throw error;
  }
}

/**
 * Update user password
 */
export async function updatePassword(passwordData, token) {
  try {
    const result = await apiRequest('/password', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(passwordData),
    });
    return result;
  } catch (error) {
    console.error('Failed to update password:', error);
    throw error;
  }
}

/**
 * Get dashboard statistics
 */
export async function getDashboardStats(token) {
  try {
    const stats = await apiRequest('/dashboard/stats', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return stats;
  } catch (error) {
    console.error('Failed to fetch dashboard stats:', error);
    throw error;
  }
}

/**
 * Get top selling products
 */
export async function getTopSellingProducts(token) {
  try {
    const products = await apiRequest('/dashboard/top-selling', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return products;
  } catch (error) {
    console.error('Failed to fetch top selling products:', error);
    throw error;
  }
}

/**
 * Create a new product
 */
export async function createProduct(productData, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }

    const formData = new FormData();
    
    // Handle arrays (colors, sizes) - must use array notation for FormData
    if (productData.colors && Array.isArray(productData.colors)) {
      productData.colors.forEach(color => {
        formData.append('colors[]', color);
      });
    }
    
    if (productData.sizes && Array.isArray(productData.sizes)) {
      productData.sizes.forEach(size => {
        formData.append('sizes[]', size);
      });
    }
    
    // Handle color_images - send as JSON string (backend decodes it)
    if (productData.color_images && typeof productData.color_images === 'object') {
      formData.append('color_images', JSON.stringify(productData.color_images));
    }
    
    // Handle color_stock - send as JSON string (backend decodes it)
    if (productData.color_stock && typeof productData.color_stock === 'object') {
      formData.append('color_stock', JSON.stringify(productData.color_stock));
      console.log('✓✓✓ color_stock added:', productData.color_stock);
    }
    
    // Handle color_sold - send as JSON string (backend decodes it)
    if (productData.color_sold && typeof productData.color_sold === 'object') {
      formData.append('color_sold', JSON.stringify(productData.color_sold));
      console.log('✓✓✓ color_sold added:', productData.color_sold);
    }
    
    // Handle variant_stock - send as JSON string (backend decodes it)
    if (productData.variant_stock && typeof productData.variant_stock === 'object') {
      formData.append('variant_stock', JSON.stringify(productData.variant_stock));
      console.log('✓✓✓ variant_stock added:', productData.variant_stock);
    }
    
    // Handle variant_sold - send as JSON string (backend decodes it)
    if (productData.variant_sold && typeof productData.variant_sold === 'object') {
      formData.append('variant_sold', JSON.stringify(productData.variant_sold));
      console.log('✓✓✓ variant_sold added:', productData.variant_sold);
    }
    
    // Add all other text fields - Skip arrays (already handled above)
    Object.keys(productData).forEach(key => {
      // Skip files and arrays - they're handled separately
      if (key !== 'images' && key !== 'videos' && key !== 'existing_images' && key !== 'existing_videos' && 
          key !== 'colors' && key !== 'sizes' && key !== 'color_images' && key !== 'color_stock' && key !== 'color_sold' &&
          key !== 'variant_stock' && key !== 'variant_sold') {
        const value = productData[key];
        // Convert boolean and numbers to strings for FormData
        // CRITICAL: Handle 0 and false explicitly (they are valid values!)
        if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0');
        } else if (typeof value === 'number') {
          // Always send numbers, including 0
          formData.append(key, String(value));
        } else if (value !== null && value !== undefined && value !== '') {
          // Handle strings and other types (but not null/undefined/empty string)
          formData.append(key, String(value));
        }
      }
    });
    
    // Add image files (new uploads) - only send if there are actual File objects
    if (productData.images && Array.isArray(productData.images) && productData.images.length > 0) {
      const fileObjects = productData.images.filter(file => file instanceof File);
      console.log(`📤 Preparing to send ${fileObjects.length} image files`);
      if (fileObjects.length > 0) {
        fileObjects.forEach((file, index) => {
          // Validate file before appending
          if (!(file instanceof File)) {
            console.error(`❌ Invalid file at index ${index}:`, file);
            return;
          }
          
          // Log file details for debugging
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          console.log(`📤 Appending image ${index + 1}/${fileObjects.length}:`, {
            name: file.name,
            extension: fileExtension,
            size: file.size,
            sizeMB: (file.size / (1024 * 1024)).toFixed(2),
            type: file.type,
            lastModified: file.lastModified,
            isFile: file instanceof File
          });
          
          // Append file if it has a valid size (let backend validate file type)
          if (file.size > 0) {
            formData.append('images[]', file, file.name);
            console.log(`✅ Image ${index + 1} appended to FormData`);
          } else {
            console.error(`❌ Invalid file at index ${index}: file size is 0 or invalid`);
          }
        });
      } else {
        console.warn('⚠️ No valid File objects found in productData.images array');
      }
    } else {
      console.log('ℹ️ No images to send (productData.images is empty or not an array)');
    }
    
    // Add video files (new uploads) - only send if there are actual File objects
    if (productData.videos && Array.isArray(productData.videos) && productData.videos.length > 0) {
      const fileObjects = productData.videos.filter(file => file instanceof File);
      if (fileObjects.length > 0) {
        fileObjects.forEach((file, index) => {
          if (file instanceof File && file.size > 0) {
            formData.append('videos[]', file, file.name);
          }
        });
      }
    }
    
          // Use fetch directly for FormData (don't use apiRequest which expects JSON)
          const url = `${getApiBaseURL()}/products`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        console.error('❌ Parsed Error JSON:', errorJson);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
        
        // Include validation errors if present
        if (errorJson.errors) {
          console.error('❌ Validation Errors:', errorJson.errors);
          const errorMessages = Object.values(errorJson.errors).flat();
          const allErrors = errorMessages.join('. ');
          errorMessage = errorMessage + (errorMessage.endsWith('.') ? ' ' : '. ') + allErrors;
          console.error('❌ All Error Messages:', allErrors);
        }
        
        // Handle 401 specifically
        if (response.status === 401) {
          // Clear invalid token
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          errorMessage = 'Your session has expired. Please log in again.';
        }
      } catch (e) {
        console.error('❌ Error parsing error response:', e);
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to create product:', error);
    throw error;
  }
}

/**
 * Update an existing product
 */
export async function updateProduct(id, productData, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }

    const formData = new FormData();
    
    // CRITICAL: Add cost_price FIRST and EXPLICITLY before anything else
    // This ensures it's always present, even if the loop misses it
    // Handle cost_price - it's required and can be 0
    if ('cost_price' in productData) {
      const costPrice = productData.cost_price;
      // cost_price can be 0, so we need to check for null/undefined specifically
      if (costPrice === null || costPrice === undefined) {
        throw new Error('Cost price is required and must be a valid number');
      }
      // Convert to number first to handle string "0" or 0
      const costPriceNum = typeof costPrice === 'number' ? costPrice : parseFloat(costPrice);
      if (isNaN(costPriceNum) || costPriceNum < 0) {
        throw new Error('Cost price must be a valid number greater than or equal to 0');
      }
      // Always append, even if 0
      formData.append('cost_price', String(costPriceNum));
      console.log('✓✓✓ cost_price added explicitly:', costPriceNum);
    } else {
      throw new Error('Cost price is required but was not provided in productData');
    }
    
    // CRITICAL: Add price explicitly BEFORE the loop to ensure it's always sent
    if ('price' in productData) {
      const priceValue = productData.price;
      if (priceValue !== null && priceValue !== undefined && priceValue !== '') {
        const priceNum = typeof priceValue === 'number' ? priceValue : parseFloat(priceValue);
        if (!isNaN(priceNum) && isFinite(priceNum)) {
          formData.append('price', String(priceNum));
          console.log(`✓✓✓ Price added explicitly to FormData: ${priceNum}`);
        } else {
          console.warn(`⚠️ Price is NaN or invalid: ${priceValue}`);
        }
      } else {
        console.warn(`⚠️ Price is null/undefined/empty: ${priceValue}`);
      }
    }
    
    // Handle arrays (colors, sizes) - must use array notation for FormData
    if (productData.colors && Array.isArray(productData.colors)) {
      productData.colors.forEach(color => {
        formData.append('colors[]', color);
      });
    }
    
    if (productData.sizes && Array.isArray(productData.sizes)) {
      productData.sizes.forEach(size => {
        formData.append('sizes[]', size);
      });
    }
    
    // Handle color_images - send as JSON string (backend decodes it)
    if (productData.color_images && typeof productData.color_images === 'object') {
      formData.append('color_images', JSON.stringify(productData.color_images));
    }
    
    // Handle color_stock - send as JSON string (backend decodes it)
    if (productData.color_stock && typeof productData.color_stock === 'object') {
      formData.append('color_stock', JSON.stringify(productData.color_stock));
      console.log('✓✓✓ color_stock added:', productData.color_stock);
    }
    
    // Handle color_sold - send as JSON string (backend decodes it)
    if (productData.color_sold && typeof productData.color_sold === 'object') {
      formData.append('color_sold', JSON.stringify(productData.color_sold));
      console.log('✓✓✓ color_sold added:', productData.color_sold);
    }
    
    // Handle variant_stock - send as JSON string (backend decodes it)
    if (productData.variant_stock && typeof productData.variant_stock === 'object') {
      formData.append('variant_stock', JSON.stringify(productData.variant_stock));
      console.log('✓✓✓ variant_stock added:', productData.variant_stock);
    }
    
    // Handle variant_sold - send as JSON string (backend decodes it)
    if (productData.variant_sold && typeof productData.variant_sold === 'object') {
      formData.append('variant_sold', JSON.stringify(productData.variant_sold));
      console.log('✓✓✓ variant_sold added:', productData.variant_sold);
    }
    
    // Add all other text fields - Skip arrays (already handled above)
    Object.keys(productData).forEach(key => {
      // Skip files, existing media, arrays, cost_price (already added above), and price (already added above)
      if (key !== 'images' && key !== 'videos' && key !== 'existing_images' && key !== 'existing_videos' && 
          key !== 'cost_price' && key !== 'price' && key !== 'colors' && key !== 'sizes' && key !== 'color_images' &&
          key !== 'color_stock' && key !== 'color_sold' && key !== 'variant_stock' && key !== 'variant_sold') {
        const value = productData[key];
        
        // Convert boolean and numbers to strings for FormData
        // CRITICAL: Handle 0 and false explicitly (they are valid values!)
        if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0');
        } else if (typeof value === 'number') {
          // Always send numbers, including 0 and NaN should be handled
          if (isNaN(value)) {
            console.warn(`⚠️ Skipping NaN value for ${key}`);
          } else {
            formData.append(key, String(value));
          }
        } else if (value !== null && value !== undefined && value !== '') {
          // Handle strings and other types (but not null/undefined/empty string)
          formData.append(key, String(value));
        }
      }
    });
    
    // Add image files (new uploads) - only send if there are actual File objects
    if (productData.images && Array.isArray(productData.images) && productData.images.length > 0) {
      const fileObjects = productData.images.filter(file => file instanceof File);
      console.log(`📤 Preparing to send ${fileObjects.length} image files for update`);
      if (fileObjects.length > 0) {
        fileObjects.forEach((file, index) => {
          // Validate file before appending
          if (!(file instanceof File)) {
            console.error(`❌ Invalid file at index ${index}:`, file);
            return;
          }
          
          // Log file details for debugging
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          console.log(`📤 Appending image ${index + 1}/${fileObjects.length}:`, {
            name: file.name,
            extension: fileExtension,
            size: file.size,
            sizeMB: (file.size / (1024 * 1024)).toFixed(2),
            type: file.type,
            lastModified: file.lastModified,
            isFile: file instanceof File
          });
          
          // Append file if it has a valid size (let backend validate file type)
          if (file.size > 0) {
            formData.append('images[]', file, file.name);
            console.log(`✅ Image ${index + 1} appended to FormData`);
          } else {
            console.error(`❌ Invalid file at index ${index}: file size is 0 or invalid`);
          }
        });
      }
    }
    
    // Add existing images (URLs to keep)
    if (productData.existing_images && Array.isArray(productData.existing_images) && productData.existing_images.length > 0) {
      productData.existing_images.forEach((url, index) => {
        if (typeof url === 'string' && url) {
          formData.append(`existing_images[${index}]`, url);
        }
      });
    }
    
    // Add video files (new uploads) - only send if there are actual File objects
    if (productData.videos && Array.isArray(productData.videos) && productData.videos.length > 0) {
      const fileObjects = productData.videos.filter(file => file instanceof File);
      if (fileObjects.length > 0) {
        fileObjects.forEach((file) => {
          formData.append('videos[]', file);
        });
      }
    }
    
    // Add existing videos (URLs to keep)
    if (productData.existing_videos && Array.isArray(productData.existing_videos) && productData.existing_videos.length > 0) {
      productData.existing_videos.forEach((url, index) => {
        if (typeof url === 'string' && url) {
          formData.append(`existing_videos[${index}]`, url);
        }
      });
    }
    
    // Debug: Log all FormData entries before sending
    console.log('📋 FormData entries before sending:');
    const imageEntries = [];
    for (const [key, value] of formData.entries()) {
      if (key === 'price' || key === 'cost_price') {
        console.log(`  ${key}: ${value} (${typeof value})`);
      }
      if (key.startsWith('images')) {
        imageEntries.push({ key, value: value instanceof File ? `File: ${value.name}` : value, type: typeof value, isFile: value instanceof File });
      }
    }
    if (imageEntries.length > 0) {
      console.log('📸 Image entries in FormData:', imageEntries);
    }
    
    // Use POST with _method=PUT for FormData updates (Laravel standard way)
    // PUT requests with multipart/form-data can have issues parsing all fields correctly
    formData.append('_method', 'PUT');
    
    const url = `${getApiBaseURL()}/products/${id}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
        
        // Include validation errors if present
        if (errorJson.errors) {
          const errorMessages = Object.entries(errorJson.errors).map(([field, messages]) => {
            return Array.isArray(messages) ? messages.join(', ') : String(messages);
          });
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('. ') + (errorJson.message ? `. ${errorJson.message}` : '');
          }
        }
        
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          errorMessage = 'Your session has expired. Please log in again.';
        }
      } catch (e) {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to update product:', error);
    throw error;
  }
}

/**
 * Delete a product
 */
export async function deleteProduct(id, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }

    const response = await apiRequest(`/products/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    return response;
  } catch (error) {
    console.error(`Failed to delete product ${id}:`, error);
    throw error;
  }
}

const apiService = {
  getProducts,
  getAdminProducts,
  getProduct,
  getCategories,
  register,
  login,
  logout,
  getMe,
  createProduct,
  updateProduct,
  deleteProduct,
  getDashboardStats,
  getTopSellingProducts,
};

/**
 * Get all users (admin only)
 */
export async function getAllUsers(token) {
  try {
    const users = await apiRequest('/admin/users', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return users;
  } catch (error) {
    console.error('Failed to fetch users:', error);
    throw error;
  }
}

/**
 * Get a specific user (admin only)
 */
export async function getUser(userId, token) {
  try {
    const user = await apiRequest(`/admin/users/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return user;
  } catch (error) {
    console.error(`Failed to fetch user ${userId}:`, error);
    throw error;
  }
}

/**
 * Create a new user (admin only)
 */
export async function createUser(userData, token) {
  try {
    const result = await apiRequest('/admin/users', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    return result;
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
}

/**
 * Update a user (admin only)
 */
export async function updateUser(userId, userData, token) {
  try {
    const result = await apiRequest(`/admin/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    return result;
  } catch (error) {
    console.error(`Failed to update user ${userId}:`, error);
    throw error;
  }
}

/**
 * Delete a user (admin only)
 */
export async function deleteUser(userId, token) {
  try {
    const result = await apiRequest(`/admin/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error(`Failed to delete user ${userId}:`, error);
    throw error;
  }
}

/**
 * Get all roles
 */
export async function getRoles(token) {
  try {
    const roles = await apiRequest('/roles', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return roles;
  } catch (error) {
    console.error('Failed to fetch roles:', error);
    throw error;
  }
}

/**
 * Fetch orders for the courier dashboard
 */
export async function getCourierOrders(token) {
  try {
    const orders = await apiRequest('/courier/orders', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return orders;
  } catch (error) {
    console.error('Failed to fetch courier orders:', error);
    throw error;
  }
}

/**
 * Get all couriers (admin only)
 */
export async function getCouriers(token) {
  try {
    const couriers = await apiRequest('/admin/couriers', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return couriers;
  } catch (error) {
    console.error('Failed to fetch couriers:', error);
    throw error;
  }
}

/**
 * Assign order to a courier (admin only)
 */
export async function assignCourierToOrder(orderId, courierId, token) {
  try {
    const result = await apiRequest(`/admin/orders/${orderId}/assign-courier`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ courier_id: courierId }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to assign courier to order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Courier dispatches order (picks up)
 */
export async function dispatchOrder(orderId, token) {
  try {
    const result = await apiRequest(`/courier/orders/${orderId}/dispatch`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error(`Failed to dispatch order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Courier marks order as delivered
 */
export async function markOrderDelivered(orderId, deliveryProofData, token) {
  try {
    const result = await apiRequest(`/courier/orders/${orderId}/delivered`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deliveryProofData),
    });
    return result;
  } catch (error) {
    console.error(`Failed to mark order ${orderId} as delivered:`, error);
    throw error;
  }
}

/**
 * Courier marks order as returned/undeliverable
 */
export async function markOrderReturned(orderId, reason, token) {
  try {
    const result = await apiRequest(`/courier/orders/${orderId}/return`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason: reason || 'Unable to deliver' }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to mark order ${orderId} as returned:`, error);
    throw error;
  }
}

/**
 * Get all orders (admin only)
 */
export async function getAllOrders(token, statusFilter = 'all') {
  try {
    const endpoint = statusFilter === 'all' 
      ? '/admin/orders' 
      : `/admin/orders?status=${statusFilter}`;
    const orders = await apiRequest(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return orders;
  } catch (error) {
    console.error('Failed to fetch orders:', error);
    throw error;
  }
}

/**
 * Get orders for the current user (regular users)
 */
export async function getOrders(token, statusFilter = 'all') {
  try {
    const endpoint = statusFilter === 'all' 
      ? '/orders' 
      : `/orders?status=${statusFilter}`;
    const orders = await apiRequest(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return orders;
  } catch (error) {
    console.error('Failed to fetch orders:', error);
    throw error;
  }
}

/**
 * Get a specific order
 */
export async function getOrder(orderId, token, isAdmin = false) {
  try {
    const endpoint = isAdmin 
      ? `/admin/orders/${orderId}?admin=true`
      : `/orders/${orderId}`;
    const order = await apiRequest(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return order;
  } catch (error) {
    console.error(`Failed to fetch order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Update an order (admin only)
 */
export async function updateOrder(orderId, orderData, token, isAdmin = false, status = null) {
  try {
    const endpoint = isAdmin 
      ? `/admin/orders/${orderId}`
      : `/orders/${orderId}`;
    
    // Handle case where orderData might be a string (status) or an object
    let data;
    if (status) {
      // If status is provided separately, merge it with orderData
      data = typeof orderData === 'object' && orderData !== null 
        ? { ...orderData, status } 
        : { status };
    } else if (typeof orderData === 'string') {
      // If orderData is a string, treat it as status
      data = { status: orderData };
    } else {
      // Otherwise use orderData as-is
      data = orderData || {};
    }
    
    const result = await apiRequest(endpoint, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return result;
  } catch (error) {
    console.error(`Failed to update order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Create a new order
 */
export async function createOrder(orderData, token) {
  try {
    const result = await apiRequest('/orders', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });
    return result;
  } catch (error) {
    console.error('Failed to create order:', error);
    throw error;
  }
}

/**
 * Cancel an order
 */
export async function cancelOrder(orderId, token) {
  try {
    const result = await apiRequest(`/orders/${orderId}/cancel`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error(`Failed to cancel order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Soft delete an order (super admin only)
 */
export async function deleteOrder(orderId, token) {
  try {
    const result = await apiRequest(`/admin/orders/${orderId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error(`Failed to delete order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Confirm delivery with OTP
 */
export async function confirmDelivery(orderId, otp, token) {
  try {
    const result = await apiRequest(`/orders/${orderId}/confirm-delivery`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ otp }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to confirm delivery for order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Resend OTP for delivery
 */
export async function resendOtp(orderId, token) {
  try {
    const result = await apiRequest(`/orders/${orderId}/resend-otp`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error(`Failed to resend OTP for order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Update refund status (admin only)
 */
export async function updateRefundStatus(orderId, status, token) {
  try {
    const result = await apiRequest(`/admin/orders/${orderId}/refund`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to update refund status for order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Prepare payment - uses existing order_id
 * Order is already created with pending_payment status
 */
export async function preparePayment(orderData, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/orders/prepare-payment', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });
    return result;
  } catch (error) {
    console.error('Failed to prepare payment:', error);
    throw error;
  }
}

/**
 * Get unprocessed payments (payments without orders) - admin only
 */
export async function getUnprocessedPayments(token) {
  try {
    const result = await apiRequest('/admin/payments/unprocessed', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error('Failed to fetch unprocessed payments:', error);
    throw error;
  }
}

/**
 * Recover order from payment - manually create order if callback failed
 */
export async function recoverOrderFromPayment(reference, token) {
  try {
    const result = await apiRequest('/orders/recover-from-payment', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reference }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to recover order from payment ${reference}:`, error);
    throw error;
  }
}

/**
 * Mark payment as failed (admin only)
 */
export async function markPaymentAsFailed(reference, reason, token) {
  try {
    const result = await apiRequest('/admin/payments/mark-failed', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reference, reason }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to mark payment ${reference} as failed:`, error);
    throw error;
  }
}

/**
 * Verify password for admin actions
 */
export async function verifyPassword(password, token) {
  try {
    const result = await apiRequest('/auth/verify-password', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });
    return result;
  } catch (error) {
    console.error('Failed to verify password:', error);
    throw error;
  }
}

/**
 * Initialize Hubtel payment using payment reference from preparePayment
 */
export async function sendPaymentOtp(orderId, phone, token) {
  try {
    const result = await apiRequest('/orders/send-payment-otp', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id: orderId,
        phone: phone,
      }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to send payment OTP:`, error);
    throw error;
  }
}

export async function verifyPaymentOtp(orderId, phone, otp, token) {
  try {
    const result = await apiRequest('/orders/verify-payment-otp', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id: orderId,
        phone: phone,
        otp: otp,
      }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to verify payment OTP:`, error);
    throw error;
  }
}

export async function initializeHubtelPayment(paymentReference, token, paymentMethod = 'card', phone = null, network = null, cardDetails = null) {
  try {
    const payload = { payment_reference: paymentReference };
    
    if (paymentMethod === 'mobile_money') {
      payload.payment_method = 'mobile_money';
      if (phone) {
        payload.phone = phone;
        payload.customerMobileNumber = phone; // Also send as customerMobileNumber for compatibility
      }
      if (network) {
        payload.network = network;
        // Map network to channel format if needed
        const networkToChannel = {
          'mtn': 'mtn-gh',
          'vodafone': 'vodafone-gh',
          'airteltigo': 'airtel-gh',
          'airtel': 'airtel-gh',
        };
        if (networkToChannel[network.toLowerCase()]) {
          payload.channel = networkToChannel[network.toLowerCase()];
        } else {
          payload.channel = network; // Use as-is if already in channel format
        }
      }
    } else {
      payload.payment_method = 'card';
      if (cardDetails && typeof cardDetails === 'object') {
        if (cardDetails.card_number) payload.card_number = cardDetails.card_number;
        if (cardDetails.expiry_month) payload.expiry_month = cardDetails.expiry_month;
        if (cardDetails.expiry_year) payload.expiry_year = cardDetails.expiry_year;
        if (cardDetails.cvv) payload.cvv = cardDetails.cvv;
      }
    }
    
    console.log('Initializing payment with payload:', {
      payment_reference: paymentReference,
      payment_method: paymentMethod,
      has_phone: !!phone,
      phone_preview: phone ? phone.substring(0, 5) + '****' : 'none',
      network: network,
    });
    
    const result = await apiRequest('/orders/initialize-payment', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return result;
  } catch (error) {
    console.error(`Failed to initialize Hubtel payment:`, error);
    // Preserve the original error message
    if (error.message) {
      throw new Error(error.message);
    }
    throw error;
  }
}

export async function submitCardPaymentOtp(chargeReference, otp, token) {
  try {
    const result = await apiRequest('/orders/card-payment/submit-otp', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ charge_reference: chargeReference, otp }),
    });
    return result;
  } catch (error) {
    console.error('Failed to submit card payment OTP:', error);
    throw error;
  }
}

/**
 * Get support tickets (user sees own, admin sees all)
 */
export async function getSupportTickets(token, isAdmin = false, status = 'all', priority = 'all') {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const endpoint = isAdmin ? '/admin/support/tickets' : '/support/tickets';
    const params = new URLSearchParams();
    if (status !== 'all') params.append('status', status);
    if (priority !== 'all') params.append('priority', priority);
    const queryString = params.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    const tickets = await apiRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return tickets;
  } catch (error) {
    console.error('Failed to fetch support tickets:', error);
    throw error;
  }
}

/**
 * Get a single support ticket
 */
export async function getSupportTicket(ticketId, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const ticket = await apiRequest(`/support/tickets/${ticketId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return ticket;
  } catch (error) {
    console.error(`Failed to fetch support ticket ${ticketId}:`, error);
    throw error;
  }
}

/**
 * Create a new support ticket
 */
export async function createSupportTicket(ticketData, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/support/tickets', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ticketData),
    });
    return result;
  } catch (error) {
    console.error('Failed to create support ticket:', error);
    throw error;
  }
}

/**
 * Update a support ticket (admin only)
 */
export async function updateSupportTicket(ticketId, ticketData, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest(`/admin/support/tickets/${ticketId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ticketData),
    });
    return result;
  } catch (error) {
    console.error(`Failed to update support ticket ${ticketId}:`, error);
    throw error;
  }
}

/**
 * Get support ticket statistics (admin only)
 */
export async function getSupportStats(token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const stats = await apiRequest('/admin/support/stats', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return stats;
  } catch (error) {
    console.error('Failed to fetch support statistics:', error);
    throw error;
  }
}

/**
 * Reply to a support ticket (both customers and admins can reply)
 */
export async function replyToSupportTicket(ticketId, message, token, isAdmin = false) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const endpoint = isAdmin ? `/admin/support/tickets/${ticketId}/reply` : `/support/tickets/${ticketId}/reply`;
    const result = await apiRequest(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to reply to support ticket ${ticketId}:`, error);
    throw error;
  }
}

/**
 * Get notifications for the authenticated user
 */
export async function getNotifications(token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/notifications', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return result;
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
    throw error;
  }
}

/**
 * Get unread notification count
 */
export async function getUnreadNotificationCount(token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/notifications/unread-count', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return result;
  } catch (error) {
    // Silently handle network errors to avoid console spam during background polling
    const isNetworkError = error instanceof TypeError || 
                          (error.message && (
                            error.message.includes('Failed to fetch') || 
                            error.message.includes('NetworkError') ||
                            error.message.includes('Network request failed')
                          ));
    
    // Only log non-network errors
    if (!isNetworkError) {
      console.error('Failed to fetch unread notification count:', error);
    }
    // Always throw the error so callers can handle it
    throw error;
  }
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(notificationId, token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest(`/notifications/${notificationId}/read`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return result;
  } catch (error) {
    console.error(`Failed to mark notification ${notificationId} as read:`, error);
    throw error;
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/notifications/read-all', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return result;
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
    throw error;
  }
}

/**
 * Get transactions with filters
 */
export async function getTransactions(token, filters = {}) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const queryParams = new URLSearchParams();
    if (filters.type) queryParams.append('type', filters.type);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.date_filter) queryParams.append('date_filter', filters.date_filter);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    
    const queryString = queryParams.toString();
    const endpoint = `/admin/transactions${queryString ? `?${queryString}` : ''}`;
    
    const result = await apiRequest(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return result;
  } catch (error) {
    console.error('Failed to fetch transactions:', error);
    throw error;
  }
}

/**
 * Get transaction statistics
 */
export async function getTransactionStatistics(token, dateFilter = 'all') {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const queryParams = new URLSearchParams();
    if (dateFilter !== 'all') {
      queryParams.append('date_filter', dateFilter);
    }
    
    const queryString = queryParams.toString();
    const endpoint = `/admin/transactions/statistics${queryString ? `?${queryString}` : ''}`;
    
    const result = await apiRequest(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return result;
  } catch (error) {
    console.error('Failed to fetch transaction statistics:', error);
    throw error;
  }
}

/**
 * Create a transaction (sale or payout)
 */
export async function createTransaction(token, transactionData) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/admin/transactions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(transactionData),
    });
    return result;
  } catch (error) {
    console.error('Failed to create transaction:', error);
    throw error;
  }
}

/**
 * Update a transaction
 */
export async function updateTransaction(token, transactionId, updateData) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest(`/admin/transactions/${transactionId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    return result;
  } catch (error) {
    console.error(`Failed to update transaction ${transactionId}:`, error);
    throw error;
  }
}

/**
 * Bulk update transactions
 */
export async function bulkUpdateTransactions(token, transactionIds, status, hubtelDate = null, payoutDate = null) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/admin/transactions/bulk-update', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        transaction_ids: transactionIds,
        status: status,
        hubtel_transfer_date: hubtelDate,
        payout_date: payoutDate,
      }),
    });
    return result;
  } catch (error) {
    console.error('Failed to bulk update transactions:', error);
    throw error;
  }
}

/**
 * Record a sale from an order
 */
export async function recordSaleFromOrder(token, orderId, notes = null) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest(`/admin/transactions/orders/${orderId}/record-sale`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notes }),
    });
    return result;
  } catch (error) {
    console.error(`Failed to record sale from order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Record a payout to business owner
 */
export async function recordPayout(token, amount, payoutDate = null, notes = null) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/admin/transactions/payout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ amount, payout_date: payoutDate, notes }),
    });
    return result;
  } catch (error) {
    console.error('Failed to record payout:', error);
    throw error;
  }
}

/**
 * Sync existing orders to transactions (backfill for orders without transaction records)
 */
export async function syncOrdersToTransactions(token) {
  try {
    if (!token) {
      throw new Error('Authentication token is required');
    }
    const result = await apiRequest('/admin/transactions/sync-orders', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return result;
  } catch (error) {
    console.error('Failed to sync orders to transactions:', error);
    throw error;
  }
}

export default apiService;

